import { useQuery } from '@tanstack/react-query';
import { priceRepository } from '@/repositories/price-repository';
import { QueryKeys } from '@/consts';

export interface CoinPriceData {
	usd: number;
	usd_24h_change: number;
}

export interface PriceData {
	[coinId: string]: CoinPriceData;
}

export const usePrices = () => {
	return useQuery<PriceData>({
		queryKey: [QueryKeys.Price],
		queryFn: fetchPriceData,
		refetchInterval: 5 * 60 * 1000, // 5 minutes
		retry: 3,
		retryDelay: 1000,
		staleTime: 30 * 1000, // 30 seconds
	});
};

const fetchPriceData = async (): Promise<PriceData> => {
	try {
		const data = await priceRepository.getPrice();
		// Ensure we never return undefined
		return data || {};
	} catch (error) {
		console.warn('Failed to fetch price data:', error);
		// Return a default empty object on error to prevent query failure
		return {};
	}
};

const getCoinIdFromType = (coinType: string): string | null => {
	if (!coinType) return null;
	// This logic should align with how IDs are returned from the backend ('sui', 'usdc')
	if (coinType.toLowerCase().includes('sui')) return 'sui';
	if (coinType.toLowerCase().includes('usdc')) return 'usdc';
	return null;
};

export const useExchangeRate = (coinType?: string) => {
	const { data: priceData, isLoading, error, ...queryInfo } = usePrices();

	const getUSDPrice = () => {
		if (!coinType || !priceData || Object.keys(priceData).length === 0) {
			return undefined;
		}

		const coinId = getCoinIdFromType(coinType);
		if (!coinId) return undefined;

		return priceData[coinId]?.usd;
	};

	return {
		data: getUSDPrice(),
		isLoading,
		error,
		...queryInfo,
	};
};
